import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { TopicNetworkResponse } from '../model/contracts'

interface Props {
  data: TopicNetworkResponse
}

interface RankedRow {
  name: string
  count: number
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {title}
    </h3>
  )
}

function RankedList({ rows, emptyLabel }: { rows: RankedRow[]; emptyLabel: string }) {
  if (rows.length === 0) {
    return <p className="text-xs text-muted-foreground">{emptyLabel}</p>
  }

  return (
    <ol className="flex flex-col gap-1">
      {rows.map((row, i) => (
        <li key={row.name} className="flex items-center gap-2">
          <span className="w-4 shrink-0 text-right text-xs text-muted-foreground">
            {i + 1}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-foreground">
            {row.name}
          </span>
          <span
            className={cn(
              'shrink-0 rounded-full bg-secondary/50 px-2 py-0.5 text-xs font-medium text-muted-foreground',
            )}
          >
            {row.count.toLocaleString()}
          </span>
        </li>
      ))}
    </ol>
  )
}

export function NetworkAnalyticsPanel({ data }: Props) {
  const { t } = useTranslation('network')

  const topAuthors: RankedRow[] = [...data.nodes]
    .filter((n) => n.type === 'author')
    .sort((a, b) => b.influence - a.influence)
    .slice(0, 10)
    .map((n) => ({ name: n.label, count: n.influence }))

  const topGroups: RankedRow[] = [...data.groups]
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)
    .map((g) => ({ name: g.label, count: g.count }))

  const topKeywords: RankedRow[] = []

  const emptyLabel = t('panel.empty')

  return (
    <aside className="flex flex-col gap-6 rounded-xl border border-border bg-card px-4 py-4">
      <div>
        <SectionHeader title={t('panel.topAuthors')} />
        <RankedList rows={topAuthors} emptyLabel={emptyLabel} />
      </div>
      <div>
        <SectionHeader title={t('panel.topGroups')} />
        <RankedList rows={topGroups} emptyLabel={emptyLabel} />
      </div>
      <div>
        <SectionHeader title={t('panel.topKeywords')} />
        <RankedList rows={topKeywords} emptyLabel={emptyLabel} />
      </div>
    </aside>
  )
}
